import { AlertTriangle, CheckCircle2, Clock3, ShieldCheck, TimerReset } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';

const toneStyles = {
  CONFIRMED: 'border-signal-green/20 bg-signal-green/5 text-signal-green',
  CONFIRMED_TRANSITION: 'border-signal-blue/30 bg-signal-blue/10 text-signal-blue',
  RISK_DEVELOPING: 'border-signal-yellow/30 bg-signal-yellow/10 text-signal-yellow',
  RECOVERY_DEVELOPING: 'border-signal-green/20 bg-signal-green/5 text-signal-green',
  DISABLED: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
};

function displayDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDateTime(date);
}

export default function SignalStabilityNotice({ stability }) {
  if (!stability?.enabled && stability?.state !== 'DISABLED') return null;

  const state = stability.state || 'CONFIRMED';
  const isDeveloping = state === 'RISK_DEVELOPING' || state === 'RECOVERY_DEVELOPING';
  const Icon = isDeveloping ? Clock3 : state === 'CONFIRMED_TRANSITION' ? AlertTriangle : ShieldCheck;
  const style = toneStyles[state] || toneStyles.CONFIRMED;
  const confirmationCount = stability.confirmationCount ?? 0;
  const requiredConfirmations = stability.requiredConfirmations ?? stability.rules?.requiredConfirmations ?? 2;
  const hasPendingChange = Boolean(stability.pendingRegime || isDeveloping || confirmationCount > 0);
  const statusCardLabel = hasPendingChange ? 'Confirm Count' : 'Pending Change';
  const statusCardValue = hasPendingChange ? `${confirmationCount}/${requiredConfirmations}` : 'None';
  const statusCardSubtext = hasPendingChange ? (stability.confirmationReason || 'Waiting for repeat signal') : 'Advice is stable';
  const cooldownRemaining = Number(stability.cooldownRemainingMinutes || 0);
  const estimatedReady = displayDateTime(stability.estimatedReadyAt);
  const thresholds = stability.rules?.thresholds || {};

  return (
    <div className={`rounded-2xl border p-4 ${style}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-white/5">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-80">Advanced Signal Rules</div>
            <div className="mt-1 text-lg font-bold text-white">{stability.label || 'Signal confirmed'}</div>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-300">
              Live score can move intraday, but Bond Mentor changes user-facing advice only after confirmation, cooldown, and hysteresis rules agree. This keeps the dashboard from whipsawing between “Stay Fully Invested” and “Tighten Stops.”
            </p>
          </div>
        </div>

        <div className="grid min-w-[280px] grid-cols-2 gap-2 text-sm">
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Live Score</div>
            <div className="mt-1 font-mono text-lg font-bold text-white">{stability.liveScore ?? '—'}/100</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Confirmed</div>
            <div className="mt-1 font-mono text-lg font-bold text-white">{stability.confirmedScore ?? '—'}/100</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{statusCardLabel}</div>
            <div className="mt-1 font-mono text-lg font-bold text-white">{statusCardValue}</div>
            <div className="mt-1 text-[10px] text-slate-400 line-clamp-2">{statusCardSubtext}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Cooldown Guard</div>
            <div className="mt-1 font-mono text-lg font-bold text-white">{cooldownRemaining > 0 ? `${cooldownRemaining} min left` : `${stability.cooldownMinutes ?? 20} min`}</div>
            <div className="mt-1 text-[10px] text-slate-400">Prevents rapid reversals</div>
          </div>
        </div>
      </div>

      {isDeveloping ? (
        <div className="mt-3 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">
          <CheckCircle2 className="mr-2 inline h-4 w-4" />
          Current confirmed advice stays in place while Bond Mentor waits for the new regime to persist.
          {estimatedReady ? <span className="ml-1 text-slate-400">Earliest confirmation: {estimatedReady}.</span> : null}
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">
          <TimerReset className="mr-2 inline h-4 w-4" />
          No regime change is pending. Bond Mentor is monitoring live/fallback inputs while keeping the confirmed advice steady.
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-300 md:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-black/15 p-3">
          <div className="font-bold uppercase tracking-[0.14em] text-slate-500">Confirmation</div>
          <div className="mt-1">Needs {requiredConfirmations} repeat reading{requiredConfirmations === 1 ? '' : 's'} or {stability.rules?.confirmAfterMinutes ?? 15} minutes of persistence.</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/15 p-3">
          <div className="font-bold uppercase tracking-[0.14em] text-slate-500">Caution Band</div>
          <div className="mt-1">Enter caution at {thresholds.cautionEnter ?? stability.enterCautionAt ?? 30}+; clear caution at {thresholds.cautionExit ?? stability.exitCautionAt ?? 22} or lower.</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/15 p-3">
          <div className="font-bold uppercase tracking-[0.14em] text-slate-500">Emergency Rule</div>
          <div className="mt-1">Danger can bypass cooldown when emergency red bypass is enabled.</div>
        </div>
      </div>
    </div>
  );
}
